/** @jsx h */
import { h } from 'preact'
import Icon from './icons/icons'
import Button from './button'
import clsx from 'clsx'
import { useLabel } from '../use-label'
import { useGlobal } from '../use-global'
import useAnalytics from '../use-analytics'
import { isRetailer } from '../utils'
import { useForm } from 'react-hook-form'
import parsePhoneNumber from 'libphonenumber-js'
import dayjs from 'dayjs'
import { useEffect } from 'preact/hooks'

const CardStore = ({ boutiqueData: boutique, selectedCountry, onToggle, toggled, isPreSelectedBoutique = false }) => {
  const {
    additionalHoursText,
    city,
    phone,
    isVirtualEnabled,
    openingHours,
    name,
    underLockdown,
    commercialName,
    postalCode,
    shippingState,
    street,
    boutiqueType,
    wholesalerCustomerGroup
  } = boutique
  const t = useLabel()
  const analytics = useAnalytics()
  const [{ selectedBoutique, canvasConfig }, dispatch] = useGlobal()

  useEffect(() => {
    /**
     * If no boutique is selected and there is a boutiqueId configured then select that boutique
     * If either isPickUp or povNGCS are true, then the first step can not be edited
     *  */
    if (!selectedBoutique.id && isPreSelectedBoutique) {
      select()
      if (canvasConfig.isPickUp || canvasConfig.isClickNCollect || canvasConfig.povNGCS) {
        dispatch({ type: 'set-step-disabled', payload: 0 })
      }
    }
  }, [selectedBoutique.id, isPreSelectedBoutique, canvasConfig.povESFY, canvasConfig.povNGCS])

  const isOpenClass = toggled ? 'rcc_c_is-open' : ''
  const { handleSubmit } = useForm()
  const select = handleSubmit(() => {
    dispatch({
      type: 'go-to-1',
      payload: {
        ...boutique
      }
    })
    canvasConfig.onSectionExpanded({
      selectorClassName: 'rcc_c_card-step-visit',
      index: 1
    })
    analytics.trackStep('baa_boutique')
  })

  let parsedPhoneNumber = null
  try {
    parsedPhoneNumber = phone && parsePhoneNumber(phone, selectedCountry)
  } catch (err) {
    console.log(err)
  }

  const boutiqueInformation = (...args) => args.filter(Boolean).join(', ')

  return (
    <div className='rcc_c_card-store rcc-border-b rcc-border-gray-very-light last:rcc-border-b-0'>
      <div
        className={clsx(
          `rcc-group rcc_c_card-store-header rcc-flex js-accordion-header rcc-cursor-pointer rcc-justify-between rcc-items-start rcc-py-5 ${isOpenClass}`,
          { 'rcc-flex-row-reverse': canvasConfig.direction === 'rtl' }
        )}
        onClick={onToggle}
      >
        <div
          className={clsx('rcc_c_card-store-wrapper-info rcc-font-extralight', {
            'rcc-text-right': canvasConfig.direction === 'rtl'
          })}
        >
          <p id='commercialName' className='rcc_c_card-store-title rcc-m-0 rcc-font-normal'>
            {commercialName || name}
          </p>
          {isRetailer({ boutiqueType, wholesalerCustomerGroup }) && (
            <span className='rcc-inline-block rcc-bg-gray-very-light rcc-text-gray rcc-px-2 rcc-font-semibold rcc-my-1'>
              {t('Select_a_Boutique.SB_Retailer')}
            </span>
          )}

          <p className='rcc-m-0'>{selectedCountry === 'JP' ? postalCode : street}</p>
          <p className='rcc-m-0'>
            {selectedCountry === 'JP'
              ? `${shippingState} ${city} ${street}`
              : boutiqueInformation(
                  city,
                  (selectedCountry === 'US' || selectedCountry === 'CA') && shippingState,
                  postalCode
                )}
          </p>
        </div>
        <div className='rcc-flex rcc-flex-col rcc-justify-between rcc-gap-2 rcc-self-stretch rcc-items-center'>
          <button
            type='button'
            aria-labelledby='commercialName'
            aria-expanded={!!isOpenClass}
            className='rcc-h-[28px] rcc-bg-transparent rcc-cursor-pointer'
          >
            <div className={clsx('rcc_c_card-store-icon rcc-bg-text', isOpenClass)}></div>
          </button>
          <div className='rcc-flex rcc-justify-between rcc-gap-2'>
            <Icon name='store' stroke={!underLockdown ? '#222' : '#e6e6e6'} />
            {isVirtualEnabled && <Icon name='videocamera' />}
          </div>
        </div>
      </div>

      {isOpenClass && (
        <div
          className={clsx('rcc_c_card-store-content js-accordion-content rcc-text-text', isOpenClass, {
            'rcc-mb-8': isOpenClass,
            'rcc-overflow-hidden': !isOpenClass
          })}
        >
          {phone && (
            <div
              className={clsx('rcc-flex rcc-items-center rcc-gap-5 rcc-mb-6 rcc-mt-1', {
                'rcc-flex-row-reverse': canvasConfig.direction === 'rtl'
              })}
            >
              <Icon name='phone' className='rcc-w-3.5' />
              <a
                href={parsedPhoneNumber ? parsedPhoneNumber.getURI() : `tel:${phone}`}
                className='rcc-flex rcc-items-center rcc-text-gray rcc-font-light rcc-text-sm rcc-tracking-widest rcc-no-underline hover:rcc-underline'
              >
                {parsedPhoneNumber && parsedPhoneNumber.formatInternational()}
              </a>
            </div>
          )}

          {openingHours.length > 0 && (
            <div
              className={clsx('rcc-flex rcc-items-start rcc-gap-5', {
                'rcc-flex-row-reverse': canvasConfig.direction === 'rtl'
              })}
            >
              <Icon name='time' className='rcc-translate-y-[3px]' />
              <div className='rcc-w-2/3'>
                {openingHours
                  .slice(0, 7)
                  .filter(el => !el.isClosed)
                  .map(el => {
                    const startAt = new Date(new Date().toISOString().substring(0, 11) + el.startT.substring(0, 5))
                    const endAt = new Date(new Date().toISOString().substring(0, 11) + el.endT.substring(0, 5))
                    return (
                      <div
                        key={el.day}
                        className={
                          'rcc-flex rcc-justify-between rcc-text-gray rcc-font-light rcc-text-sm rcc-mb-2 last:rcc-mb-0'
                        }
                      >
                        <p
                          className={clsx('rcc-m-0', {
                            'rcc-tracking-widest': canvasConfig.locale !== 'ar'
                          })}
                        >
                          {el.day}
                        </p>
                        <p className='rcc-m-0 rcc-tracking-widest'>
                          {canvasConfig.locale !== 'ar' &&
                            `${dayjs(startAt).format('LT')} - ${dayjs(endAt).format('LT')}`}
                          {canvasConfig.locale === 'ar' &&
                            `${dayjs(startAt).format('hh:mm a')} - ${dayjs(endAt).format('hh:mm a')}`}
                        </p>
                      </div>
                    )
                  })}
                {additionalHoursText && (
                  <p
                    className={clsx('rcc-text-gray rcc-font-light rcc-text-sm rcc-mt-4', {
                      'rcc-tracking-widest': canvasConfig.locale !== 'ar'
                    })}
                  >
                    {additionalHoursText}
                  </p>
                )}
              </div>
            </div>
          )}

          {underLockdown && (
            <p className='rcc-my-4 rcc-text-base rcc-font-normal text-center'>
              {t('Global.Boutique_Lockdown_Message')}
            </p>
          )}
        </div>
      )}

      <div className='rcc-mb-5'>
        <Button className={!isVirtualEnabled && 'rcc_c_card-store-btn'} onClick={select}>
          {t('Select_a_Boutique.SB_Select_This_Boutique')}
        </Button>
      </div>
    </div>
  )
}

export default CardStore
